import { ChangeEvent, useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import moment from "moment";

import { getBase64, isFileSupported } from "../../utils/file";

import check from "../../images/check.svg";
import trash from "../../images/trash.svg";
import sync from "../../images/sync.svg";
import pdf from "../../images/pdf.png";

type ButtonDocumentProps = {
  title: string;
  valid: string;
  value: string | undefined;
  onChange: (base64: string, formatted: string) => void;
  required?: boolean;
  isLabelOptionnal?: boolean;
};

interface DocumentData {
  base64: string;
  formatted: string;
}

function ButtonDocument({
  title,
  valid,
  onChange,
  value,
  required,
  isLabelOptionnal = true,
}: ButtonDocumentProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [currentDocument, setCurrentDocument] = useState<DocumentData | null>(
    value ? { base64: value, formatted: "" } : null
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentDocument) {
      onChange(currentDocument.base64, currentDocument.formatted);
    } else {
      onChange("", "");
    }
  }, [currentDocument]);

  return (
    <div className="button-picture-container">
      {currentDocument ? (
        <>
          <div onClick={() => setCurrentDocument(null)}>
            <img className="button-picture-icon" alt="trash" src={trash} />
          </div>
          <div className="button-picture-viewer">
            <img
              height={
                currentDocument.base64.includes("image/png") ||
                currentDocument.base64.includes("image/jpg") ||
                currentDocument.base64.includes("image/jpeg")
                  ? ""
                  : "36"
              }
              src={
                currentDocument.base64.includes("image/png") ||
                currentDocument.base64.includes("image/jpg") ||
                currentDocument.base64.includes("image/jpeg")
                  ? currentDocument.base64
                  : pdf
              }
              alt={title}
            />
            <div onClick={() => inputRef.current?.click()}>
              <img
                className="button-picture-icon-right"
                alt="sync"
                src={sync}
              />
            </div>
          </div>
        </>
      ) : null}
      <button
        className={currentDocument ? "button-picture" : "button-document"}
        onClick={() => {
          ReactGA.event({
            category: "click_document",
            action: "open_file",
            value: 0,
          });
          inputRef.current?.click();
        }}
      >
        <span>
          {currentDocument && valid ? valid : title}{" "}
          {required ? "(Obligatoire)" : isLabelOptionnal ? "(Facultatif)" : ""}
        </span>
        {currentDocument ? <img alt="camera" src={check} /> : null}
      </button>
      <input
        style={{ position: "absolute", top: -1000, left: -1000 }}
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,.pdf"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files[0]) {
            let file = e.target.files[0];

            if (!isFileSupported(file)) {
              window.umami?.track("unsupported_file_format", { type: file.type, name: file.name, component: "ButtonDocument" });
              alert("Format non supporté. Formats acceptés : JPG, PNG, GIF, WebP, HEIC, PDF.");
              return;
            }

            const time = file.lastModified
              ? moment(file.lastModified)
              : moment();

            const formatted = time.format("DD/MM/YYYY HH:mm:ss");

            getBase64(file)
              .then((result: any) => {
                ReactGA.event({
                  category: "click_document",
                  action: "close_file_true",
                  value: 1,
                });
                setCurrentDocument({ base64: result.base64, formatted });
              })
              .catch(() => {
                ReactGA.event({
                  category: "click_document",
                  action: "close_file_error",
                  value: 3,
                });
              });
          } else {
            ReactGA.event({
              category: "click_document",
              action: "close_file_false",
              value: 2,
            });
          }
        }}
      />
    </div>
  );
}

export default ButtonDocument;
